import { useState, useEffect } from 'react'
import { Button, Input, Label, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from '../components/ui'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'
import { Plus, Mail, Phone, Copy, Check, Users, MoreHorizontal, Search, RefreshCw } from 'lucide-react'

interface TeamMember {
  id: string
  name: string
  email: string
  phone: string
  role: string
  membership_role: string
  created_at: number
  last_login_at?: number
  locations: string[]
}

interface Invitation {
  id: string
  contact: string
  contact_type: 'email' | 'phone'
  role: string
  status: 'pending' | 'accepted' | 'expired'
  inviter_name: string
  token: string
  expires_at: number
  created_at: number
}

interface Team {
  id: string
  name: string
  owner_user_id: string
  tier: number
  created_at: number
  member_count: number
  location_count: number
  user_role: 'TEAM_ADMIN' | 'MEMBER'
}

export default function People() {
  const { currentCompany } = useCompany()
  const { user } = useAuth()
  const [members, setMembers] = useState<TeamMember[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [regeneratingInvite, setRegeneratingInvite] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateTeam, setShowCreateTeam] = useState(false)
  const [creatingTeam, setCreatingTeam] = useState(false)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [newInvitation, setNewInvitation] = useState({
    contact: '',
    type: 'email' as 'email' | 'phone',
    role: 'MEMBER',
    teamId: '', // Add team selection
    countryCode: '+1'
  })
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [newTeamForm, setNewTeamForm] = useState({
    name: '',
    industry: '',
    timezone: ''
  })

  // Common country codes
  const countryCodes = [
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+91', country: 'IN', flag: '🇮🇳' },
    { code: '+86', country: 'CN', flag: '🇨🇳' },
    { code: '+81', country: 'JP', flag: '🇯🇵' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+55', country: 'BR', flag: '🇧🇷' },
    { code: '+52', country: 'MX', flag: '🇲🇽' },
    { code: '+27', country: 'ZA', flag: '🇿🇦' },
    { code: '+234', country: 'NG', flag: '🇳🇬' },
    { code: '+254', country: 'KE', flag: '🇰🇪' },
  ]

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const fetchedTeams = data.teams || []
        setTeams(fetchedTeams)
        
        // Find a team where the user is a member
        const userTeam = fetchedTeams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
          setNewInvitation(prev => ({ ...prev, teamId: userTeam.id }))
        } else if (fetchedTeams.length > 0) {
          setCurrentTeamId(fetchedTeams[0].id)
          setNewInvitation(prev => ({ ...prev, teamId: fetchedTeams[0].id }))
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch team members and invitations
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchTeamData()
  }, [currentTeamId])

  const fetchTeamData = async () => {
    if (!currentTeamId) return

    try {
      setLoading(true)
      setError(null)

      // Fetch team members and invitations in parallel
      const [membersResponse, invitationsResponse] = await Promise.all([
        fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams/${currentTeamId}/members`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        ),
        fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/invitations?team_id=${currentTeamId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )
      ])

      if (!membersResponse.ok) {
        throw new Error('Failed to fetch team members')
      }

      if (!invitationsResponse.ok) {
        throw new Error('Failed to fetch invitations')
      }

      const membersData = await membersResponse.json()
      const invitationsData = await invitationsResponse.json()

      setMembers(membersData.members || [])
      setInvitations(invitationsData.invitations || [])
    } catch (err) {
      console.error('Failed to fetch team data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load team data')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newInvitation.contact.trim()) {
      setError('Please enter an email or phone number')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      // Add country code to phone numbers if not already present
      const finalContact = newInvitation.type === 'phone' && !newInvitation.contact.startsWith('+')
        ? newInvitation.countryCode + newInvitation.contact.replace(/[^\d]/g, '')
        : newInvitation.contact.trim()

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contact: finalContact,
            type: newInvitation.type,
            role: newInvitation.role,
            team_id: newInvitation.teamId || currentCompany?.id
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create invitation')
      }

      // Reset form and refresh data
      setNewInvitation({ contact: '', type: 'email', role: 'MEMBER', teamId: '', countryCode: '+1' })
      setShowInviteModal(false)
      setShowCountryDropdown(false)
      await fetchTeamData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invitation')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newTeamForm.name.trim()) {
      setError('Team name is required')
      return
    }

    setCreatingTeam(true)
    setError(null)

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/teams`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newTeamForm)
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create team')
      }

      const data = await response.json()
      
      // Update teams list and select the new team for invitation
      await fetchTeamData()
      setNewInvitation(prev => ({ ...prev, teamId: data.team.id }))
      setNewTeamForm({ name: '', industry: '', timezone: '' })
      setShowCreateTeam(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team')
    } finally {
      setCreatingTeam(false)
    }
  }

  const generateInviteLink = (invitation: Invitation) => {
    const baseUrl = window.location.origin
    return `${baseUrl}/invite/${invitation.token}`
  }

  const copyInviteLink = async (invitation: Invitation) => {
    const link = generateInviteLink(invitation)
    try {
      await navigator.clipboard.writeText(link)
      setCopiedToken(invitation.id)
      setTimeout(() => setCopiedToken(null), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
    }
  }

  const regenerateInvitation = async (invitationId: string) => {
    setRegeneratingInvite(invitationId)
    setError(null)

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${invitationId}/regenerate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to regenerate invitation')
      }

      // Refresh the invitations list to show the new invitation
      await fetchTeamData()
    } catch (err) {
      console.error('Failed to regenerate invitation:', err)
      setError(err instanceof Error ? err.message : 'Failed to regenerate invitation')
    } finally {
      setRegeneratingInvite(null)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadge = (invitation: Invitation) => {
    const now = Date.now() / 1000
    if (invitation.status === 'accepted') {
      return <Badge variant="success">Accepted</Badge>
    } else if (invitation.expires_at < now) {
      return <Badge variant="danger">Expired</Badge>
    } else {
      return <Badge variant="warning">Pending</Badge>
    }
  }

  const getRoleBadge = (role: string) => {
    const roleColors = {
      'TEAM_ADMIN': 'bg-purple-100 text-purple-800',
      'MEMBER': 'bg-blue-100 text-blue-800',
      'ADMIN': 'bg-red-100 text-red-800',
      'OWNER': 'bg-green-100 text-green-800'
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        roleColors[role as keyof typeof roleColors] || 'bg-gray-100 text-gray-800'
      }`}>
        {role.replace('TEAM_', '')}
      </span>
    )
  }

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone?.includes(searchTerm)
  )

  const filteredInvitations = invitations.filter(invitation =>
    invitation.contact.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white shadow rounded-lg">
            <div className="h-12 bg-gray-200 rounded-t-lg mb-2"></div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 mb-1"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error && !showInviteModal) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load team data</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button 
          onClick={fetchTeamData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (!currentCompany) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please select a company to view team members.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Team Members</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage your team members and send invitations
          </p>
        </div>
        <Button 
          onClick={() => setShowInviteModal(true)}
          className="bg-blue-600 text-white hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Invite Member
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>{members.length} members</span>
          <span>•</span>
          <span>{invitations.filter(i => i.status === 'pending').length} pending invitations</span>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-medium text-gray-900">Active Members</h2>
        </div>
        
        {filteredMembers.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Users className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No team members found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'No members match your search.' : 'Start by inviting your first team member.'}
            </p>
            {!searchTerm && (
              <Button 
                onClick={() => setShowInviteModal(true)}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Locations</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    <div>
                      <div className="font-medium text-gray-900">{member.name || 'Unnamed User'}</div>
                      {member.id === user?.id && (
                        <span className="text-xs text-blue-600 font-medium">You</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {member.email && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="w-3 h-3 mr-1" />
                          {member.email}
                        </div>
                      )}
                      {member.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="w-3 h-3 mr-1" />
                          {member.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(member.membership_role)}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {member.locations && member.locations.length > 0 ? (
                        member.locations.map((location, idx) => (
                          <div key={idx} className="text-sm text-gray-600">
                            {location}
                          </div>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400">All locations</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatDate(member.created_at)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {member.last_login_at 
                      ? formatDate(member.last_login_at)
                      : 'Never'
                    }
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Pending Invitations</h2>
              <p className="text-sm text-gray-600 mt-1">
                Use "Regenerate" to create a new invitation link and invalidate the old one
              </p>
            </div>
          </div>
          
          {filteredInvitations.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No invitations match your search.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">{invitation.contact}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {invitation.contact_type === 'email' ? (
                          <Mail className="w-4 h-4 mr-1 text-gray-400" />
                        ) : (
                          <Phone className="w-4 h-4 mr-1 text-gray-400" />
                        )}
                        {invitation.contact_type}
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(invitation.role)}</TableCell>
                    <TableCell>{getStatusBadge(invitation)}</TableCell>
                    <TableCell>{invitation.inviter_name}</TableCell>
                    <TableCell>{formatDate(invitation.created_at)}</TableCell>
                    <TableCell>
                      {invitation.status === 'pending' && invitation.expires_at > Date.now() / 1000 && (
                        <div className="flex items-center space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyInviteLink(invitation)}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 h-8"
                          >
                            {copiedToken === invitation.id ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Copied</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Copy</span>
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => regenerateInvitation(invitation.id)}
                            disabled={regeneratingInvite === invitation.id}
                            className="text-orange-600 hover:text-orange-900 px-2 py-1 h-8"
                            title="Generate new invitation link (invalidates current one)"
                          >
                            {regeneratingInvite === invitation.id ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                <span className="hidden sm:inline">Regenerating...</span>
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Regenerate</span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Invite Team Member</h2>
              <button
                onClick={() => {
                  setShowInviteModal(false)
                  setError(null)
                  setNewInvitation({ contact: '', type: 'email', role: 'MEMBER', teamId: '', countryCode: '+1' })
                  setShowCountryDropdown(false)
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateInvitation} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-type">Contact Method</Label>
                    <Select
                      id="contact-type"
                      value={newInvitation.type}
                      onChange={(e) => {
                        setNewInvitation(prev => ({ ...prev, type: e.target.value as 'email' | 'phone', contact: '' }))
                        setShowCountryDropdown(false)
                      }}
                      className="mt-1"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select
                      id="role"
                      value={newInvitation.role}
                      onChange={(e) => setNewInvitation(prev => ({ ...prev, role: e.target.value }))}
                      className="mt-1"
                    >
                      <option value="MEMBER">Member</option>
                      <option value="TEAM_ADMIN">Admin</option>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="team">Assign to Team</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCreateTeam(true)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Create Team
                    </Button>
                  </div>
                  <Select
                    id="team"
                    value={newInvitation.teamId}
                    onChange={(e) => setNewInvitation(prev => ({ ...prev, teamId: e.target.value }))}
                    className="mt-1"
                  >
                    <option value="">Select a team...</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.member_count} members)
                      </option>
                    ))}
                  </Select>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose which team this member will join. You can create a new team if needed.
                  </p>
                </div>

                <div>
                  <Label htmlFor="contact">
                    {newInvitation.type === 'email' ? 'Email Address' : 'Phone Number'}
                  </Label>
                  <div className="mt-1">
                    {newInvitation.type === 'phone' ? (
                      <div className="flex gap-2">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                            className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center gap-1 h-[42px]"
                          >
                            <span>{countryCodes.find(c => c.code === newInvitation.countryCode)?.flag || '🌍'}</span>
                            <span className="font-medium">{newInvitation.countryCode}</span>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {showCountryDropdown && (
                            <div className="absolute z-20 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                              {countryCodes.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onClick={() => {
                                    setNewInvitation(prev => ({ ...prev, countryCode: country.code }))
                                    setShowCountryDropdown(false)
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <span>{country.flag}</span>
                                  <span className="font-medium">{country.code}</span>
                                  <span className="text-gray-500 text-sm">{country.country}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Phone className="h-5 w-5 text-gray-400" />
                          </div>
                          <Input
                            id="contact"
                            type="tel"
                            value={newInvitation.contact}
                            onChange={(e) => {
                              // Only allow digits, spaces, hyphens, and parentheses
                              const value = e.target.value.replace(/[^\d\s\-\(\)]/g, '')
                              setNewInvitation(prev => ({ ...prev, contact: value }))
                            }}
                            className="pl-10"
                            placeholder="555 123 4567"
                            required
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="contact"
                          type="email"
                          value={newInvitation.contact}
                          onChange={(e) => setNewInvitation(prev => ({ ...prev, contact: e.target.value }))}
                          className="pl-10"
                          placeholder="user@example.com"
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowInviteModal(false)
                    setError(null)
                    setNewInvitation({ contact: '', type: 'email', role: 'MEMBER', teamId: '', countryCode: '+1' })
                  setShowCountryDropdown(false)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  {submitting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create New Team</h2>
              <button
                onClick={() => {
                  setShowCreateTeam(false)
                  setError(null)
                  setNewTeamForm({ name: '', industry: '', timezone: '' })
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateTeam} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="teamName">Team Name *</Label>
                  <Input
                    id="teamName"
                    value={newTeamForm.name}
                    onChange={(e) => setNewTeamForm({ ...newTeamForm, name: e.target.value })}
                    placeholder="Enter team name"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry (Optional)</Label>
                  <Input
                    id="industry"
                    value={newTeamForm.industry}
                    onChange={(e) => setNewTeamForm({ ...newTeamForm, industry: e.target.value })}
                    placeholder="e.g., Construction, Retail, etc."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="timezone">Timezone (Optional)</Label>
                  <Input
                    id="timezone"
                    value={newTeamForm.timezone}
                    onChange={(e) => setNewTeamForm({ ...newTeamForm, timezone: e.target.value })}
                    placeholder="e.g., America/New_York"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateTeam(false)
                    setError(null)
                    setNewTeamForm({ name: '', industry: '', timezone: '' })
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingTeam || !newTeamForm.name.trim()}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  {creatingTeam ? 'Creating...' : 'Create Team'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}